import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const HeartIconComponent = ({ dim = 20, tint = '#fff' }) => (
  <svg
    width={dim}
    height={dim}
    viewBox="0 0 24 24"
    fill="none"
    stroke={tint}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'block' }}
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
  </svg>
);

const TagLateral = ({ yPos, xPos, anim, icono, titulo, valor, estiloColor }) => (
  <div
    className={`med-tag ${anim}`}
    style={{
      position: 'absolute',
      left: xPos || '40px',
      bottom: yPos || '320px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 18px',
      borderRadius: 14,
      background: estiloColor?.bk || 'rgba(255,255,255,0.10)',
      backdropFilter: 'blur(8px)',
      border: `1px solid ${estiloColor?.brd || 'rgba(255,255,255,0.18)'}`,
      zIndex: 20
    }}
  >
    <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {icono}
    </div>
    <div>
      <div style={{ fontSize: 10, opacity: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>{titulo}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{valor}</div>
    </div>
  </div>
);

const ModuloInscripcion = () => {
  const navigate = useNavigate();
  const [vistaActiva, setVistaActiva] = useState('lista');
  const [usuarios, setUsuarios] = useState([]);
  const [editandoId, setEditandoId] = useState(null);
  const [filtroRol, setFiltroRol] = useState(null);

  const [tipoPuesto, setTipoPuesto] = useState('Doctor');
  const [loading, setLoading] = useState(false);
  const [mostrarClave, setMostrarClave] = useState(false);

  const [campos, setCampos] = useState({
    nomUser: '',
    apeUser: '',
    dniUser: '',
    claveUser: '',
    especialidadMed: ''
  });

  const [esViewportAncho, setEsViewportAncho] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const resizeControl = () => setEsViewportAncho(window.innerWidth < 768);
    window.addEventListener('resize', resizeControl);
    return () => window.removeEventListener('resize', resizeControl);
  }, []);

  useEffect(() => {
    if (vistaActiva === 'lista') {
      obtenerUsuarios();
    }
  }, [vistaActiva]);

  const URL_BASE = 'https://backend-46yr.onrender.com/api';

  const obtenerUsuarios = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URL_BASE}/usuarios`);
      const data = await response.json();
      if (data.success) {
        setUsuarios(data.data || []);
      } else {
        setUsuarios([]);
      }
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const usuariosFiltrados = filtroRol
    ? usuarios.filter(u => u.rol === filtroRol)
    : usuarios;

  const cambiarEstado = async (dni, estadoActual) => {
    try {
      const response = await fetch(`${URL_BASE}/usuarios/${dni}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activo: estadoActual == 1 ? 0 : 1 })
      });
      const data = await response.json();
      if (data.success) {
        obtenerUsuarios();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const ejecutarEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;

    try {
      const response = await fetch(`${URL_BASE}/usuarios/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();

      if (response.ok || data.success) {
        alert('Usuario eliminado correctamente');
        obtenerUsuarios();
      } else {
        alert('Error al eliminar el usuario');
      }
    } catch (error) {
      console.error(error);
      alert('Error de conexión al eliminar');
    }
  };

  const activarEdicion = (user) => {
    setEditandoId(user.dni);
    setCampos({
      nomUser: user.nombre || '',
      apeUser: user.apellido || '',
      dniUser: user.dni || '',
      claveUser: '',
      especialidadMed: user.especialidad || ''
    });
    setTipoPuesto(user.rol || 'Doctor');
    setVistaActiva('formulario');
  };

  const limpiarFormulario = () => {
    setEditandoId(null);
    setCampos({
      nomUser: '',
      apeUser: '',
      dniUser: '',
      claveUser: '',
      especialidadMed: ''
    });
    setTipoPuesto('Doctor');
  };

  const ejecutarRegistroOEdicion = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (campos.dniUser.length !== 8) {
      alert('El DNI debe tener exactamente 8 números');
      return;
    }

    if (!editandoId || campos.claveUser) {
      const regexPassword = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.#_-])[A-Za-z\d@$!%*?&.#_-]{8,}$/;
      if (!regexPassword.test(campos.claveUser)) {
        alert('La contraseña debe tener mínimo 8 caracteres, mayúscula, minúscula, número y símbolo');
        return;
      }
    }

    setLoading(true);
    const endpoint = editandoId ? `${URL_BASE}/usuarios/${editandoId}` : `${URL_BASE}/register`;
    const metodo = editandoId ? 'PUT' : 'POST';

    const cuerpoPeticion = {
      nombre: campos.nomUser,
      apellido: campos.apeUser,
      dni: campos.dniUser,
      rol: tipoPuesto,
      especialidad: campos.especialidadMed
    };

    if (campos.claveUser) {
      cuerpoPeticion.password = campos.claveUser;
    }

    try {
      const response = await fetch(endpoint, {
        method: metodo,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cuerpoPeticion)
      });

      const data = await response.json();

      if (response.ok || data.success) {
        alert(editandoId ? 'Usuario actualizado correctamente' : 'Usuario registrado correctamente');
        limpiarFormulario();
        setVistaActiva('lista');
      } else {
        alert(data.message || 'Error al procesar la solicitud');
      }
    } catch (error) {
      console.error(error);
      alert('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const areas = ['Medicina General', 'Cardiología', 'Pediatría', 'Neurología', 'Traumatología', 'Ginecología', 'Cirugía General', 'Odontología', 'Medicina Interna', 'Otro'];
  const tiposRol = [
    { id: 'Doctor', tx: 'Doctor', em: '👨‍⚕️' },
    { id: 'Admision', tx: 'Admisión', em: '🗂️' },
    { id: 'Admin', tx: 'Admin', em: '⚙️' }
  ];

  const coords = {
    pc: { a: '400px', b: '320px', c: '240px' },
    mb: { a: '22vh', b: '16vh', c: '10vh' }
  };

  const manejarInput = (e) => {
    const { name, value } = e.target;
    if (name === 'dniUser') {
      setCampos({ ...campos, [name]: value.replace(/\D/g, '') });
      return;
    }
    if (name === 'nomUser' || name === 'apeUser') {
      setCampos({ ...campos, [name]: value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '') });
      return;
    }
    setCampos({ ...campos, [name]: value });
  };

  const cerrarSesion = () => {
    localStorage.clear();
    navigate('/');
  };

  const calcY = (key) => (esViewportAncho ? coords.mb[key] : coords.pc[key]);

  return (
    <div style={mEstilos.wrapGeneral}>
      <style>{`
        @keyframes floatEffect1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes floatEffect2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes floatEffect3 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .m1{animation:floatEffect1 4s ease-in-out infinite}
        .m2{animation:floatEffect2 5s ease-in-out infinite}
        .m3{animation:floatEffect3 3.5s ease-in-out infinite}
        .tit-dinamico { font-weight: 900; margin: 0 0 6px; font-size: clamp(24px, 5vw, 34px); line-height: 1.05; color: #0f2944; }
        .panel-form { width: 100%; }
        .inner-form { width: 100%; max-width: 650px; padding: 18px; box-sizing: border-box; margin: 0 auto; }
        
        .tabla-crud { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 14px; }
        .tabla-crud th { background-color: #f1f5f9; color: #334155; padding: 10px; text-align: left; font-weight: 700; border-bottom: 2px solid #e2e8f0; }
        .tabla-crud td { padding: 10px; border-bottom: 1px solid #e2e8f0; color: #475569; }
        .contenedor-tabla { width: 100%; overflow-x: auto; background: #fff; border-radius: 8px; border: 1px solid #e2e8f0; }

        @media (max-width: 767px) {
          .tit-dinamico { font-size: 24px !important; }
          .inner-form { max-width: 100% !important; padding: 10px !important; }
          .med-tag { padding: 10px 14px !important; border-radius: 12px !important; }
        }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      <nav style={mEstilos.navbar}>
        <div style={mEstilos.navBrand}>
          <span style={{ fontSize: 24 }}>⚕️</span>
          <span style={{ fontWeight: 800, color: '#1e3a5f' }}>VitalScan Admin</span>
        </div>

        <div style={mEstilos.navTabs}>
          <button
            style={{ ...mEstilos.navBtn, ...(vistaActiva === 'lista' ? mEstilos.navBtnActive : {}) }}
            onClick={() => setVistaActiva('lista')}
          >
            📋 Ver Usuarios
          </button>
          <button
            style={{ ...mEstilos.navBtn, ...(vistaActiva === 'formulario' && !editandoId ? mEstilos.navBtnActive : {}) }}
            onClick={() => { limpiarFormulario(); setVistaActiva('formulario'); }}
          >
            ➕ Registrar Nuevo
          </button>
        </div>

        <button type="button" onClick={cerrarSesion} style={mEstilos.btnSalir}>
          <span>🚪</span> Salir
        </button>
      </nav>

      <div style={mEstilos.wrapPpal}>
        {!esViewportAncho && (
          <div style={mEstilos.ladoAzul}>
            <div style={mEstilos.capaContent}>
              <div style={{ marginTop: '10px' }}>
                <h2 className="tit-dinamico" style={{ color: '#fff' }}>Panel de Control</h2>
                <p style={{ fontSize: '14px', opacity: 0.85, maxWidth: '360px' }}>
                  Administración global de accesos, roles y especialidades del personal.
                </p>
              </div>
            </div>
            <TagLateral anim="m1" yPos="140px" xPos="40px" icono={<HeartIconComponent />} titulo="Pulso promedio" valor="78 bpm" estiloColor={{ bk: 'rgba(255,255,255,0.12)', brd: 'rgba(255,255,255,0.2)' }} />
            <TagLateral anim="m2" yPos={calcY('b')} xPos="70px" icono={<span style={{ fontSize: 18 }}>🌡️</span>} titulo="Temperatura" valor="36.5 °C" estiloColor={{ bk: 'rgba(245,158,11,0.2)', brd: 'rgba(245,158,11,0.4)' }} />
            <TagLateral anim="m3" yPos={calcY('c')} xPos="40px" icono={<span style={{ fontSize: 18 }}>💧</span>} titulo="Oxigenación" valor="98%" estiloColor={{ bk: 'rgba(255,255,255,0.12)', brd: 'rgba(255,255,255,0.2)' }} />
            <div style={mEstilos.shapeOverlay} />
          </div>
        )}

        <div className="panel-form" style={mEstilos.ladoBlanco}>
          <div className="inner-form" style={mEstilos.boxForm}>

            <div style={{ marginBottom: 20 }}>
              <h2 className="tit-dinamico">
                {vistaActiva === 'lista' ? 'Personal Registrado' : editandoId ? 'Modificar Usuario' : 'Crear nueva cuenta'}
              </h2>
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
                {vistaActiva === 'lista' ? 'Visualiza, edita o elimina registros del sistema.' : 'Completa los datos del trabajador.'}
              </p>
            </div>

            {vistaActiva === 'lista' ? (
              <>
                <div style={{ marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setFiltroRol(null)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      backgroundColor: filtroRol === null ? '#0f2944' : '#e2e8f0',
                      color: filtroRol === null ? '#fff' : '#475569',
                      transition: 'all 0.2s'
                    }}
                  >
                    👥 Ver Todos ({usuarios.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setFiltroRol('Doctor')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      backgroundColor: filtroRol === 'Doctor' ? '#2563eb' : '#eff6ff',
                      color: filtroRol === 'Doctor' ? '#fff' : '#2563eb',
                      transition: 'all 0.2s',
                      borderBottom: filtroRol === 'Doctor' ? '3px solid #1e40af' : 'none'
                    }}
                  >
                    👨‍⚕️ Doctores ({usuarios.filter(u => u.rol === 'Doctor').length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setFiltroRol('Admision')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      backgroundColor: filtroRol === 'Admision' ? '#f59e0b' : '#fef3c7',
                      color: filtroRol === 'Admision' ? '#fff' : '#d97706',
                      transition: 'all 0.2s',
                      borderBottom: filtroRol === 'Admision' ? '3px solid #d97706' : 'none'
                    }}
                  >
                    🗂️ Admisión ({usuarios.filter(u => u.rol === 'Admision').length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setFiltroRol('Admin')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      backgroundColor: filtroRol === 'Admin' ? '#8b5cf6' : '#ede9fe',
                      color: filtroRol === 'Admin' ? '#fff' : '#7c3aed',
                      transition: 'all 0.2s',
                      borderBottom: filtroRol === 'Admin' ? '3px solid #7c3aed' : 'none'
                    }}
                  >
                    ⚙️ Administradores ({usuarios.filter(u => u.rol === 'Admin').length})
                  </button>
                </div>

                <div className="contenedor-tabla">
                  {loading ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Cargando registros...</div>
                  ) : usuariosFiltrados.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                      {filtroRol ? `No hay ${filtroRol === 'Admision' ? 'usuarios de admisión' : filtroRol === 'Admin' ? 'administradores' : 'doctores'} registrados.` : 'No hay usuarios registrados actualmente.'}
                    </div>
                  ) : (
                    <table className="tabla-crud">
                      <thead>
                        <tr>
                          <th>Nombre Completo</th>
                          <th>DNI</th>
                          <th>Rol</th>
                          <th>Especialidad</th>
                          <th>Estado</th>
                          <th style={{ textAlign: 'center' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usuariosFiltrados.map((u) => (
                          <tr key={u._id || u.id}>
                            <td>{`${u.nombre || ''} ${u.apellido || ''}`}</td>
                            <td>{u.dni}</td>
                            <td>
                              <span
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: 6,
                                  background: '#f1f5f9',
                                  fontWeight: 600,
                                  fontSize: 12
                                }}
                              >
                                {u.rol}
                              </span>
                            </td>
                            <td>{u.especialidad || '—'}</td>
                            <td>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 12,
                                  background: u.activo == 1 ? '#dcfce7' : '#fee2e2',
                                  color: u.activo == 1 ? '#166534' : '#991b1b'
                                }}
                              >
                                {u.activo == 1 ? '🟢 Activo' : '🔴 Desactivado'}
                              </span>
                            </td>
                            <td style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              <button
                                onClick={() => activarEdicion(u)}
                                style={{
                                  ...mEstilos.btnAccion,
                                  backgroundColor: '#eff6ff',
                                  color: '#2563eb',
                                  border: '1px solid #bfdbfe'
                                }}
                              >
                                ✏️
                              </button>

                              {u.activo == 1 ? (
                                <button
                                  onClick={() => ejecutarEliminar(u.dni)}
                                  style={{
                                    ...mEstilos.btnAccion,
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    border: '1px solid #fca5a5'
                                  }}
                                >
                                  🗑️
                                </button>
                              ) : (
                                <button
                                  onClick={() => cambiarEstado(u.dni, u.activo)}
                                  style={{
                                    ...mEstilos.btnAccion,
                                    backgroundColor: '#dcfce7',
                                    color: '#166534',
                                    border: '1px solid #bbf7d0'
                                  }}
                                >
                                  🔄 Activar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            ) : (
              <form onSubmit={ejecutarRegistroOEdicion} style={mEstilos.stackInputs}>
                <div style={mEstilos.grpInput}>
                  <label style={mEstilos.lblTxt}>Selecciona el rol</label>
                  <div style={mEstilos.filaRoles}>
                    {tiposRol.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setTipoPuesto(r.id)}
                        style={{ ...mEstilos.btRol, ...(tipoPuesto === r.id ? mEstilos.btRolOn : {}) }}
                      >
                        <span style={{ fontSize: 18 }}>{r.em}</span>
                        <span style={{ fontSize: 11, fontWeight: '700' }}>{r.tx}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={mEstilos.lblTxt}>Nombre</label>
                    <input name="nomUser" style={mEstilos.inputBox} placeholder="Nombre" value={campos.nomUser} onChange={manejarInput} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={mEstilos.lblTxt}>Apellido</label>
                    <input name="apeUser" style={mEstilos.inputBox} placeholder="Apellido" value={campos.apeUser} onChange={manejarInput} required />
                  </div>
                </div>

                {tipoPuesto === 'Doctor' && (
                  <div style={mEstilos.grpInput}>
                    <label style={mEstilos.lblTxt}>Especialidad</label>
                    <select name="especialidadMed" style={mEstilos.inputBox} value={campos.especialidadMed} onChange={manejarInput} required>
                      <option value="">Selección...</option>
                      {areas.map((e) => (
                        <option key={e} value={e}>{e}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={mEstilos.grpInput}>
                  <label style={mEstilos.lblTxt}>DNI</label>
                  <input name="dniUser" type="text" inputMode="numeric" pattern="[0-9]{8}" maxLength={8} minLength={8} style={mEstilos.inputBox} placeholder="DNI" value={campos.dniUser} onChange={manejarInput} required />
                </div>

                <div style={mEstilos.grpInput}>
                  <label style={mEstilos.lblTxt}>
                    {editandoId ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                  </label>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      name="claveUser"
                      type={mostrarClave ? "text" : "password"}
                      style={{ ...mEstilos.inputBox, width: '100%', paddingRight: '40px' }}
                      placeholder={editandoId ? "Opcional" : "Mínimo 8 caracteres"}
                      value={campos.claveUser}
                      onChange={manejarInput}
                      required={!editandoId}
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarClave(!mostrarClave)}
                      style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b', padding: 0 }}
                    >
                      {mostrarClave ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: '10px' }}>
                  {editandoId && (
                    <button
                      type="button"
                      onClick={() => { limpiarFormulario(); setVistaActiva('lista'); }}
                      style={{ ...mEstilos.btnSubmit, background: '#e2e8f0', color: '#475569', flex: 1 }}
                    >
                      Cancelar
                    </button>
                  )}
                  <button
                    type="submit"
                    style={{
                      ...mEstilos.btnSubmit,
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      flex: 2
                    }}
                    disabled={loading} d
                  >
                    {loading ? 'Procesando...' : editandoId ? 'Guardar Cambios' : 'Completar Registro'}
                  </button>
                </div>
              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

const mEstilos = {
  wrapGeneral: { 
    display: 'flex', 
    flexDirection: 'column', 
    minHeight: '100vh', 
    backgroundColor: '#f8fafc', 
    fontFamily: 'sans-serif' 
  },
  navbar: {
     height: '60px', 
     background: '#ffffff', 
     borderBottom: '1px solid #e2e8f0', 
     display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', 
      padding: '0 24px', 
      position: 'sticky', 
      top: 0, 
      zIndex: 100 
    },
  navBrand: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '8px' 
  },
  navTabs: { 
    display: 'flex', 
    gap: '10px' 
  },
  navBtn: { 
    padding: '8px 16px', 
    border: 'none', 
    background: 'transparent', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontWeight: '600', 
    fontSize: '13px', 
    color: '#64748b', 
    transition: 'all 0.2s' 
  },
  navBtnActive: {
     background: '#f1f5f9', 
     color: '#0f2944'
     },
  btnSalir: { 
    padding: '6px 14px', 
    backgroundColor: '#fee2e2', 
    color: '#991b1b', 
    border: '1px solid #fca5a5',
     borderRadius: '8px', 
     cursor: 'pointer', 
     fontSize: '12px',
      fontWeight: 'bold', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px'
     },
  wrapPpal: { 
    display: 'flex', 
    flex: 1, 
    height: 'calc(100vh - 60px)',
    overflow: 'hidden'
  },
  ladoAzul: { 
    flex: '0 0 35%',
    background: 'linear-gradient(145deg, #1e3a5f 0%, #1a5276 40%, #1a6b8a 100%)', 
    position: 'relative',
     padding: '40px', 
     color: 'white', 
     display: 'flex', 
     flexDirection: 'column' 
    },
  capaContent: { 
    zIndex: 10,
     display: 'flex', 
     flexDirection: 'column' 
    },
  ladoBlanco: { 
    flex: 1, 
    overflowY: 'auto', 
    padding: '20px', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'flex-start', 
    background: '#fff'
   },
  boxForm: { 
    display: 'flex', 
    flexDirection: 'column' 
  },
  stackInputs: { 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '8px' 
  },
  grpInput: {
     display: 'flex',
      flexDirection: 'column', 
      gap: '6px' 
    },
  lblTxt: { 
    fontSize: '12px', fontWeight: '700', 
    color: '#334155'
   },
  inputBox: {
     padding: '10px', 
     border: '1.5px solid #e2e8f0', 
     borderRadius: '10px',
      outline: 'none', fontSize: '14px', 
      boxSizing: 'border-box' 
    },
  filaRoles: {
     display: 'flex', 
     gap: '8px' 
    },
  btRol: { 
    flex: 1, 
    padding: '8px', 
    border: '2px solid #e2e8f0',
     borderRadius: '10px', background: 'white', 
     display: 'flex', 
     flexDirection: 'column',
      alignItems: 'center',
       gap: '4px',
       cursor: 'pointer', 
       fontSize: '12px' 
      },
  btRolOn: { borderColor: '#f59e0b',
     background: '#fffbeb', 
     color: '#b45309' },
  btnSubmit: { padding: '12px', 
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
     color: 'white',
      border: 'none',
       borderRadius: '10px', 
       fontWeight: '700', 
       fontSize: '14px',
        marginTop: '6px'
       },
  btnAccion: { 
    border: 'none', 
    padding: '6px 10px', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '14px'
   },
  shapeOverlay: { 
    position: 'absolute',
     width: '260px', 
     height: '260px', 
     borderRadius: '50%', 
     border: '1px solid rgba(255,255,255,0.05)',
      top: '-48px',
       right: '-48px' }
};

export default ModuloInscripcion;